import api from '../lib/axios';
import { AxiosError } from 'axios';
import { logger } from '../utils/logger';
import { tokenStorage } from '../utils/tokenStorage';
import { appendAffiliateAttribution } from '../utils/affiliateAttribution';
import type {
  LoginRequest,
  RegisterRequest,
  RefreshTokenRequest,
  SendOTPRequest,
  VerifyOTPRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  AuthResponse,
  OTPResponse,
  RefreshTokenResponse,
  ProfileResponse,
} from '../types/auth';

// API Endpoints from environment variables
const API_ENDPOINTS = {
  LOGIN: import.meta.env.VITE_API_V1_AUTH_LOGIN,
  REGISTER: import.meta.env.VITE_API_V1_AUTH_REGISTER,
  LOGOUT: import.meta.env.VITE_API_V1_AUTH_LOGOUT,
  VERIFY_EMAIL_SEND: import.meta.env.VITE_API_V1_AUTH_VERIFY_EMAIL_SEND,
  VERIFY_PHONE_SEND: import.meta.env.VITE_API_V1_AUTH_VERIFY_PHONE_SEND,
  VERIFY_OTP: import.meta.env.VITE_API_V1_AUTH_VERIFY_OTP,
  PASSWORD_RESET_REQUEST: import.meta.env.VITE_API_V1_AUTH_PASSWORD_RESET_REQUEST,
  PASSWORD_RESET_CONFIRM: import.meta.env.VITE_API_V1_AUTH_PASSWORD_RESET_CONFIRM,
  USERS_PROFILE: import.meta.env.VITE_API_V1_USERS_PROFILE || '/api/v1/users/profile',
  // PIN Endpoints
  PIN_STATUS: '/api/v1/auth/pin/status',
  PIN_VERIFY: '/api/v1/auth/pin/verify',
  PIN_SET: '/api/v1/auth/pin/set',
  PIN_CHANGE: '/api/v1/auth/pin/change',
  PIN_RESET_OTP: '/api/v1/auth/pin/reset-otp',
  PIN_RESET: '/api/v1/auth/pin/reset',
};

const authService = {
  // Login user
  login: async (credentials: LoginRequest): Promise<AuthResponse> => {
    try {
      const payload = appendAffiliateAttribution(credentials as unknown as Record<string, unknown>);
      logger.log('Login request:', payload);
      const response = await api.post<AuthResponse>(API_ENDPOINTS.LOGIN, payload);
      logger.log('Login response:', response.data);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string; message?: string }>;
      logger.error('Login error:', axiosError);
      logger.error('Login error response:', axiosError.response?.data);
      return {
        success: false,
        error:
          axiosError.response?.data?.error || axiosError.response?.data?.message || 'Login failed',
      };
    }
  },

  // Google OAuth login
  googleLogin: async (params: {
    credential?: string;
    accessToken?: string;
  }): Promise<AuthResponse> => {
    try {
      logger.log('Google login request');
      const payload = appendAffiliateAttribution(params as unknown as Record<string, unknown>);
      const response = await api.post<AuthResponse>('/api/v1/auth/google', payload);
      logger.log('Google login response:', response.data);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string; message?: string }>;
      logger.error('Google login error:', axiosError);
      return {
        success: false,
        error:
          axiosError.response?.data?.error ||
          axiosError.response?.data?.message ||
          'Google login failed',
      };
    }
  },

  // Apple OAuth login
  appleLogin: async (params: {
    authorizationCode?: string;
    identityToken?: string;
    fullName?: string;
    clientApp?: string;
    // backward-compatible aliases from existing callers
    code?: string;
    idToken?: string;
    user?: unknown;
  }): Promise<AuthResponse> => {
    try {
      logger.log('Apple login request');
      const appleUser = params.user as
        | { name?: { firstName?: string; lastName?: string }; fullName?: string }
        | undefined;
      const normalizedFullName =
        params.fullName ||
        appleUser?.fullName ||
        [appleUser?.name?.firstName, appleUser?.name?.lastName].filter(Boolean).join(' ').trim() ||
        undefined;

      const payload = appendAffiliateAttribution({
        identityToken: params.identityToken || params.idToken,
        authorizationCode: params.authorizationCode || params.code,
        fullName: normalizedFullName,
        clientApp: params.clientApp || 'web',
      } as Record<string, unknown>);
      const response = await api.post<AuthResponse>('/api/v1/auth/apple', payload, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      logger.log('Apple login response:', response.data);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string; message?: string }>;
      logger.error('Apple login error:', axiosError);
      return {
        success: false,
        error:
          axiosError.response?.data?.error ||
          axiosError.response?.data?.message ||
          'Apple login failed',
      };
    }
  },

  // Register new user
  register: async (userData: RegisterRequest): Promise<AuthResponse> => {
    try {
      const payload = appendAffiliateAttribution(userData as unknown as Record<string, unknown>);
      const response = await api.post<AuthResponse>(API_ENDPOINTS.REGISTER, payload);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string }>;
      logger.error('Registration error:', axiosError);
      return {
        success: false,
        error: axiosError.response?.data?.error || 'Registration failed',
      };
    }
  },

  // Refresh access token
  refreshToken: async (refreshData: RefreshTokenRequest): Promise<RefreshTokenResponse> => {
    // Skip refresh if offline
    if (!navigator.onLine) {
      logger.log('Offline: Skipping token refresh');
      // Return a mock success response to keep the session "alive" in the app state
      // This assumes the token is still valid or we don't care until back online
      return {
        success: true,
        accessToken: tokenStorage.getAccessToken() || '',
        refreshToken: tokenStorage.getRefreshToken() || '',
      };
    }

    try {
      logger.log('AuthService: Attempting token refresh...');
      const response = await api.post<RefreshTokenResponse>(
        '/api/v1/auth/refresh-token',
        refreshData,
      );
      logger.log('AuthService: Token refresh response:', response.data);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string; message?: string }>;
      logger.error('AuthService: Token refresh error:', {
        status: axiosError.response?.status,
        data: axiosError.response?.data,
        message: axiosError.message,
      });

      // Handle specific error cases
      if (axiosError.response?.status === 401) {
        // Throw error for 401 so AuthProvider can detect and logout immediately
        throw new Error('401: Refresh token expired or invalid');
      }

      if (axiosError.response?.status === 403) {
        return {
          success: false,
          error: 'Refresh token access denied',
        };
      }

      return {
        success: false,
        error:
          axiosError.response?.data?.error ||
          axiosError.response?.data?.message ||
          'Token refresh failed',
      };
    }
  },

  // Send OTP for phone or email verification
  sendOTP: async (otpData: SendOTPRequest): Promise<OTPResponse> => {
    try {
      // Determine endpoint based on whether email or phone is provided
      const endpoint = otpData.email
        ? API_ENDPOINTS.VERIFY_EMAIL_SEND
        : API_ENDPOINTS.VERIFY_PHONE_SEND;

      const payload = appendAffiliateAttribution(otpData as unknown as Record<string, unknown>);
      const response = await api.post<OTPResponse>(endpoint, payload);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string }>;
      logger.error('Send OTP error:', axiosError);
      return {
        success: false,
        error: axiosError.response?.data?.error || 'Failed to send OTP',
      };
    }
  },

  // Verify SMS OTP
  verifyOTP: async (otpData: VerifyOTPRequest): Promise<OTPResponse> => {
    try {
      const payload = appendAffiliateAttribution(otpData as unknown as Record<string, unknown>);
      const response = await api.post<OTPResponse>(API_ENDPOINTS.VERIFY_OTP, payload);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string }>;
      logger.error('Verify OTP error:', axiosError);
      return {
        success: false,
        error: axiosError.response?.data?.error || 'OTP verification failed',
      };
    }
  },

  // Logout user — send the current device's refreshToken so the backend only
  // revokes this session, leaving other devices' sessions intact.
  logout: async (): Promise<{ success: boolean; error?: string }> => {
    try {
      const { tokenStorage } = await import('../utils/tokenStorage');
      const refreshToken = tokenStorage.getRefreshToken();
      await api.post(API_ENDPOINTS.LOGOUT, refreshToken ? { refreshToken } : {});
      return { success: true };
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string }>;
      logger.error('Logout error:', axiosError);
      return {
        success: false,
        error: axiosError.response?.data?.error || 'Logout failed',
      };
    }
  },

  // Request password reset
  forgotPassword: async (resetData: ForgotPasswordRequest): Promise<OTPResponse> => {
    try {
      const response = await api.post<OTPResponse>(API_ENDPOINTS.PASSWORD_RESET_REQUEST, resetData);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string }>;
      logger.error('Forgot password error:', axiosError);
      return {
        success: false,
        error: axiosError.response?.data?.error || 'Password reset request failed',
      };
    }
  },

  // Confirm password reset with OTP
  resetPassword: async (resetData: ResetPasswordRequest): Promise<OTPResponse> => {
    try {
      const response = await api.post<OTPResponse>(API_ENDPOINTS.PASSWORD_RESET_CONFIRM, resetData);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string; message?: string }>;
      logger.error('Reset password error:', axiosError);
      return {
        success: false,
        error:
          axiosError.response?.data?.error ||
          axiosError.response?.data?.message ||
          'Password reset failed',
      };
    }
  },

  // Update user profile
  updateProfile: async (payload: {
    phoneNumber?: string;
    name?: string;
    email?: string;
  }): Promise<AuthResponse> => {
    try {
      const response = await api.put(API_ENDPOINTS.USERS_PROFILE, payload);
      return { success: true, data: response.data };
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string }>;
      logger.error('Update profile error:', axiosError);
      return {
        success: false,
        error: axiosError.response?.data?.error || 'Failed to update profile',
      };
    }
  },

  // Get user profile
  getUserProfile: async (): Promise<ProfileResponse> => {
    // Check offline first
    if (!navigator.onLine) {
      const offlineProfile = localStorage.getItem('offline_user_profile');
      if (offlineProfile) {
        logger.log('Returning offline user profile');
        return {
          success: true,
          data: JSON.parse(offlineProfile),
        };
      }
    }

    try {
      const response = await api.get(API_ENDPOINTS.USERS_PROFILE);

      // Cache profile for offline use
      if (response.data.user) {
        localStorage.setItem('offline_user_profile', JSON.stringify(response.data.user));
      }

      return {
        success: true,
        data: response.data.user,
      };
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string }>;
      logger.error('Get user profile error:', axiosError);
      return {
        success: false,
        error: axiosError.response?.data?.error || 'Failed to fetch user profile',
      };
    }
  },

  // Switch active role and get new tokens
  switchRole: async (role?: 'user' | 'merchant'): Promise<RefreshTokenResponse> => {
    try {
      // Some backends toggle role without a body; send role if provided
      const payload = role ? { role } : {};
      const response = await api.post<RefreshTokenResponse>('/api/v1/auth/switch-role', payload);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string; message?: string }>;
      logger.error('Switch role error:', {
        status: axiosError.response?.status,
        data: axiosError.response?.data,
        message: axiosError.message,
      });
      return {
        success: false,
        error:
          axiosError.response?.data?.error ||
          axiosError.response?.data?.message ||
          'Role switch failed',
      };
    }
  },

  deleteAccount: async (
    password?: string,
  ): Promise<{ success: boolean; message?: string; error?: string; code?: string }> => {
    try {
      const response = await api.delete(API_ENDPOINTS.USERS_PROFILE, {
        data: password ? { password } : undefined,
      });
      return response.data?.success === false ? response.data : { success: true, ...response.data };
    } catch (error) {
      const axiosError = error as AxiosError<{
        error?: string;
        message?: string;
        code?: string;
      }>;
      logger.error('Delete account error:', axiosError);
      return {
        success: false,
        code: axiosError.response?.data?.code,
        error:
          axiosError.response?.data?.error ||
          axiosError.response?.data?.message ||
          'Failed to delete account',
      };
    }
  },

  // Check PIN status
  checkPinStatus: async (): Promise<{ success: boolean; hasPin: boolean }> => {
    try {
      const response = await api.get<{ success: boolean; data: { isPinSet: boolean } }>(
        API_ENDPOINTS.PIN_STATUS,
      );
      return {
        success: response.data.success,
        hasPin: response.data.data.isPinSet,
      };
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string }>;
      logger.error('Check PIN status error:', axiosError);
      return {
        success: false,
        hasPin: false,
      };
    }
  },

  verifyPin: async (
    pin: string,
  ): Promise<{ success: boolean; message?: string; error?: string }> => {
    try {
      const response = await api.post(API_ENDPOINTS.PIN_VERIFY, { pin });
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string; message?: string }>;
      logger.error('Verify PIN error:', axiosError);
      return {
        success: false,
        error:
          axiosError.response?.data?.error ||
          axiosError.response?.data?.message ||
          'Failed to verify PIN',
      };
    }
  },

  // Set new PIN
  setPin: async (pin: string): Promise<{ success: boolean; message?: string; error?: string }> => {
    try {
      const response = await api.post(API_ENDPOINTS.PIN_SET, { pin });
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string; message?: string }>;
      logger.error('Set PIN error:', axiosError);
      return {
        success: false,
        error:
          axiosError.response?.data?.error ||
          axiosError.response?.data?.message ||
          'Failed to set PIN',
      };
    }
  },

  // Change PIN
  changePin: async (
    oldPin: string,
    newPin: string,
  ): Promise<{ success: boolean; message?: string; error?: string }> => {
    try {
      const response = await api.post(API_ENDPOINTS.PIN_CHANGE, { oldPin, newPin });
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string; message?: string }>;
      logger.error('Change PIN error:', axiosError);
      return {
        success: false,
        error:
          axiosError.response?.data?.error ||
          axiosError.response?.data?.message ||
          'Failed to change PIN',
      };
    }
  },

  // Request OTP for PIN reset
  requestPinResetOtp: async (): Promise<{
    success: boolean;
    message?: string;
    error?: string;
  }> => {
    try {
      const response = await api.post(API_ENDPOINTS.PIN_RESET_OTP);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string; message?: string }>;
      logger.error('Request PIN reset OTP error:', axiosError);
      return {
        success: false,
        error:
          axiosError.response?.data?.error ||
          axiosError.response?.data?.message ||
          'Failed to request OTP',
      };
    }
  },

  // Reset PIN with OTP
  resetPin: async (
    otp: string,
    newPin: string,
  ): Promise<{ success: boolean; message?: string; error?: string }> => {
    try {
      const response = await api.post(API_ENDPOINTS.PIN_RESET, { otp, newPin });
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string; message?: string }>;
      logger.error('Reset PIN error:', axiosError);
      return {
        success: false,
        error:
          axiosError.response?.data?.error ||
          axiosError.response?.data?.message ||
          'Failed to reset PIN',
      };
    }
  },
};

export default authService;
